import { useCallback, useState } from 'react';
import apiService from '../../api/apiService';
import { RecipeRealization } from '../../api/Types';
import {
  canDeleteRecipeRealization,
  canSubmitRecipeRealization,
  deleteRecipeRealization,
  submitRecipeRealization,
} from '../../domains/realizations/use-cases/realizationActions';

interface UseRecipeRealizationActionsParams {
  recipeId: string;
  authorId: number | null | undefined;
  canPublishRealization: boolean;
  currentAuthorRealization: RecipeRealization | null;
  commentDraft: string;
  selectedFile: File | null;
  removeCurrentImage: boolean;
  onReload: () => Promise<void>;
  onToast: (message: string, variant: 'success' | 'info') => void;
  onError: (message: string) => void;
  onCommentDraftChange: (comment: string) => void;
  onResetDraft: () => void;
}

interface UseRecipeRealizationActionsResult {
  submitting: boolean;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
  handleDelete: () => Promise<void>;
}

export const useRecipeRealizationActions = ({
  recipeId,
  authorId,
  canPublishRealization,
  currentAuthorRealization,
  commentDraft,
  selectedFile,
  removeCurrentImage,
  onReload,
  onToast,
  onError,
  onCommentDraftChange,
  onResetDraft,
}: UseRecipeRealizationActionsParams): UseRecipeRealizationActionsResult => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSubmitRecipeRealization(authorId, canPublishRealization, submitting)) {
      return;
    }

    setSubmitting(true);
    onError('');

    try {
      const result = await submitRecipeRealization({
        recipeId,
        authorId,
        canPublishRealization,
        currentAuthorRealization,
        commentDraft,
        selectedFile,
        removeCurrentImage,
        uploadRealizationImage: apiService.uploadRealizationImage,
        upsertMyRecipeRealization: apiService.upsertMyRecipeRealization,
        deleteRealizationImage: apiService.deleteRealizationImage,
      });

      if (result.shouldAbort) {
        if (result.errorMessage) {
          onError(result.errorMessage);
        }
        return;
      }

      await onReload();
      if (result.successMessage) {
        onToast(result.successMessage, 'success');
      }
      if (result.previousImageCleanupWarning) {
        console.warn(
          'Failed to delete previous realization image:',
          result.previousImageCleanupWarning
        );
      }
      if (typeof result.nextCommentDraft === 'string') {
        onCommentDraftChange(result.nextCommentDraft);
      }
      if (result.shouldResetDraft) {
        onResetDraft();
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    authorId,
    canPublishRealization,
    commentDraft,
    currentAuthorRealization,
    onCommentDraftChange,
    onError,
    onReload,
    onResetDraft,
    onToast,
    recipeId,
    removeCurrentImage,
    selectedFile,
    submitting,
  ]);

  const handleDelete = useCallback(async () => {
    if (!canDeleteRecipeRealization(currentAuthorRealization, submitting)) {
      return;
    }

    if (!window.confirm('Voulez-vous vraiment supprimer votre realisation ?')) {
      return;
    }

    setSubmitting(true);
    onError('');

    try {
      const result = await deleteRecipeRealization({
        currentAuthorRealization,
        deleteMyRecipeRealization: apiService.deleteMyRecipeRealization,
        deleteRealizationImage: apiService.deleteRealizationImage,
      });

      if (result.shouldAbort) {
        if (result.errorMessage) {
          onError(result.errorMessage);
        }
        return;
      }

      await onReload();
      if (result.imageCleanupWarning) {
        console.warn('Failed to delete realization image:', result.imageCleanupWarning);
      }
      if (result.successMessage) {
        onToast(result.successMessage, 'info');
      }
    } finally {
      setSubmitting(false);
    }
  }, [currentAuthorRealization, onError, onReload, onToast, submitting]);

  return {
    submitting,
    handleSubmit,
    handleDelete,
  };
};

export default useRecipeRealizationActions;
